import type { ProjectsApiResponse } from '@/types/project';
import { apiRequest } from '@/lib/api';

export async function fetchProjects(): Promise<ProjectsApiResponse> {
  return apiRequest('/api/projects');
}

export async function acceptInvitation(invitationId: string): Promise<{ success: boolean }> {
  // For now, redirect to the Flask invitation accept page
  window.location.href = `/project-invite/${invitationId}/join`;
  return { success: true };
}

export async function declineInvitation(invitationId: string): Promise<{ success: boolean; error?: string }> {
  return apiRequest(`/api/project/invitations/${invitationId}/decline`, {
    method: 'POST',
  });
}

export interface CreateProjectData {
  name: string;
  odoo_version: string;
  github_repo: string;
  server_choice: 'managed' | 'self-hosted';
  odoo_enterprise_code?: string;
}

export interface CreateCheckoutSessionData {
  name: string;
  odoo_version: string;
  github_repo: string;
  plan: string;
  server_choice?: string;
  cpu_type?: string;
  region?: string;
  server_plan?: string;
  developer_branches?: boolean;
  backup_storage?: boolean;
  odoo_enterprise_code?: string;
}

export async function createProjectCheckoutSession(data: CreateCheckoutSessionData): Promise<{
  success: boolean;
  url?: string;
  error?: string;
}> {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('odoo_version', data.odoo_version);
  formData.append('github_repo', data.github_repo);
  formData.append('plan', data.plan);

  if (data.server_choice) formData.append('server_choice', data.server_choice);
  if (data.cpu_type) formData.append('cpu_type', data.cpu_type);
  if (data.region) formData.append('region', data.region);
  if (data.server_plan) formData.append('server_plan', data.server_plan);
  if (data.developer_branches) formData.append('developer_branches', 'on');
  if (data.backup_storage) formData.append('backup_storage', 'on');
  if (data.odoo_enterprise_code) formData.append('odoo_enterprise_code', data.odoo_enterprise_code);

  const response = await fetch('/api/project/create-checkout-session', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to create checkout session');
  }

  return response.json();
}

export async function createProject(data: CreateProjectData): Promise<{
  success: boolean;
  project_id?: string;
  checkout_url?: string;
  error?: string;
}> {
  try {
    const response = await createProjectCheckoutSession({
      name: data.name,
      odoo_version: data.odoo_version,
      github_repo: data.github_repo,
      plan: 'pro_plan', // Default to pro plan
      server_choice: data.server_choice,
      developer_branches: false, // Could be made configurable later
      backup_storage: true, // Always include backup storage
      odoo_enterprise_code: data.odoo_enterprise_code,
    });

    if (response.success && response.url) {
      // Redirect to Stripe checkout
      window.location.href = response.url;
      // Return a promise that never resolves since we're redirecting
      return new Promise(() => {});
    } else {
      return {
        success: false,
        error: response.error || 'Failed to create checkout session',
      };
    }
  } catch (error) {
    return {
      success: false,
      error: 'Failed to create project. Please try again.',
    };
  }
}
